import type { components } from '@multaqa/api-client'
import { type UseMutationOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { companyHeader, unwrap, useFreshCompanyId, useInvalidateAccounting } from './api-internal'
import type {
  DepreciationRunResult,
  FixedAsset,
  FixedAssetDisposal,
  FixedAssetDisposeRequest,
  FixedAssetStoreRequest,
  FixedAssetUpdateRequest,
} from './api-types'

export function useCreateFixedAsset(
  companyId: string,
  options?: Omit<UseMutationOptions<FixedAsset, Error, FixedAssetStoreRequest>, 'mutationFn'>,
) {
  const invalidate = useInvalidateAccounting(companyId)
  const userOnSuccess = options?.onSuccess

  return useMutation<FixedAsset, Error, FixedAssetStoreRequest>({
    ...options,
    mutationFn: async (input) => {
      const response = await api.POST('/accounting/fixed-assets', {
        // The one cast in this file, and it is the Scramble inference gap
        // documented on FixedAssetStoreRequest above.
        body: input as unknown as components['schemas']['FixedAssetStoreRequest'],
        headers: companyHeader(companyId),
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to create the asset')
      return unwrap<FixedAsset>(response.data ?? {}, 'fixed-assets')
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidate()
      userOnSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

export function useUpdateFixedAsset(
  companyId: string,
  assetId: string,
  options?: Omit<UseMutationOptions<FixedAsset, Error, FixedAssetUpdateRequest>, 'mutationFn'>,
) {
  const invalidate = useInvalidateAccounting(companyId)
  const userOnSuccess = options?.onSuccess

  return useMutation<FixedAsset, Error, FixedAssetUpdateRequest>({
    ...options,
    mutationFn: async (input) => {
      const response = await api.PATCH('/accounting/fixed-assets/{asset}', {
        params: { path: { asset: assetId } },
        // The Scramble inference gap documented on FixedAssetUpdateRequest.
        body: input as unknown as components['schemas']['FixedAssetUpdateRequest'],
        headers: companyHeader(companyId),
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to save the asset')
      return unwrap<FixedAsset>(response.data ?? {}, 'fixed-assets')
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidate()
      userOnSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

/** Move a draft into the ledger: allocates the asset number and posts acquisition. */
export function usePlaceAssetInService(
  companyId: string,
  assetId: string,
  options?: Omit<UseMutationOptions<FixedAsset, Error, void>, 'mutationFn'>,
) {
  const invalidate = useInvalidateAccounting(companyId)
  const userOnSuccess = options?.onSuccess

  return useMutation<FixedAsset, Error, void>({
    ...options,
    mutationFn: async () => {
      const response = await api.POST('/accounting/fixed-assets/{asset}/place-in-service', {
        params: { path: { asset: assetId } },
        headers: companyHeader(companyId),
      })
      if (response.error) {
        throw new Error(response.error.message ?? 'Failed to place the asset in service')
      }
      return unwrap<FixedAsset>(response.data ?? {}, 'fixed-assets')
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidate()
      userOnSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

/** Dispose an asset; the API computes and returns the gain or loss. */
export function useDisposeFixedAsset(
  companyId: string,
  assetId: string,
  options?: Omit<
    UseMutationOptions<FixedAssetDisposal, Error, FixedAssetDisposeRequest>,
    'mutationFn'
  >,
) {
  const invalidate = useInvalidateAccounting(companyId)
  const userOnSuccess = options?.onSuccess

  return useMutation<FixedAssetDisposal, Error, FixedAssetDisposeRequest>({
    ...options,
    mutationFn: async (input) => {
      const response = await api.POST('/accounting/fixed-assets/{asset}/dispose', {
        params: { path: { asset: assetId } },
        body: input,
        headers: companyHeader(companyId),
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to dispose the asset')
      return unwrap<FixedAssetDisposal>(response.data ?? {}, 'fixed-assets')
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidate()
      userOnSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

/** Dry-run disposal preview; it never posts or invalidates queries. */
export function useDisposePreview(
  companyId: string,
  assetId: string,
  options?: Omit<
    UseMutationOptions<FixedAssetDisposal, Error, Omit<FixedAssetDisposeRequest, 'dry_run'>>,
    'mutationFn'
  >,
) {
  return useMutation<FixedAssetDisposal, Error, Omit<FixedAssetDisposeRequest, 'dry_run'>>({
    ...options,
    mutationFn: async (input) => {
      const response = await api.POST('/accounting/fixed-assets/{asset}/dispose', {
        params: { path: { asset: assetId } },
        body: { ...input, dry_run: true },
        headers: companyHeader(companyId),
      })
      if (response.error)
        throw new Error(response.error.message ?? 'Failed to preview the disposal')
      return unwrap<FixedAssetDisposal>(response.data ?? {}, 'fixed-assets')
    },
  })
}

/** API-backed dry-run preview of the depreciation run. */
export function useDepreciationPreview(
  companyId: string | null,
  options?: Omit<
    UseMutationOptions<DepreciationRunResult, Error, { fiscal_period_id: string }>,
    'mutationFn'
  >,
) {
  const currentCompany = useFreshCompanyId(companyId)

  return useMutation<DepreciationRunResult, Error, { fiscal_period_id: string }>({
    ...options,
    mutationFn: async (body) => {
      const response = await api.POST('/accounting/depreciation-runs', {
        headers: { 'X-Company-Id': currentCompany() },
        body: { fiscal_period_id: body.fiscal_period_id, dry_run: true },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to preview the run')
      return unwrap<DepreciationRunResult>(response.data ?? {}, 'depreciation-runs')
    },
  })
}

/** The real run. Invalidates the whole accounting prefix — assets, journal, periods all change. */
export function useDepreciationRun(
  companyId: string | null,
  options?: Omit<
    UseMutationOptions<DepreciationRunResult, Error, { fiscal_period_id: string }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  const userOnSuccess = options?.onSuccess

  return useMutation<DepreciationRunResult, Error, { fiscal_period_id: string }>({
    ...options,
    mutationFn: async (body) => {
      const response = await api.POST('/accounting/depreciation-runs', {
        headers: { 'X-Company-Id': currentCompany() },
        body: { fiscal_period_id: body.fiscal_period_id, dry_run: false },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to post depreciation')
      return unwrap<DepreciationRunResult>(response.data ?? {}, 'depreciation-runs')
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      if (companyId) {
        void queryClient.invalidateQueries({
          queryKey: ['companies', companyId, 'accounting'],
        })
      }
      userOnSuccess?.(data, variables, onMutateResult, context)
    },
  })
}
